<shared-drive-selector>
  <div class="shared-drive-selector-container w-full flex flex-col gap-3">
    <!-- Header. A plain heading rather than a card: this is a short list of
         choices, and a full card above it cost more height than the rows. -->
    <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <h3 class="text-base font-semibold flex items-center gap-2">
        <i class="icon-folder-open text-primary" aria-hidden="true"></i>
        {locale?.t('admin_drive_select_title')}
      </h3>
      <p class="text-xs text-base-content/70">
        {locale?.t(props.mode === 'folder' ? 'admin_drive_select_subtitle_folder' : 'admin_drive_select_subtitle')}
      </p>
    </div>

    <!-- Loading State -->
    <div if="{state.loading}" class="flex justify-center py-12">
      <div class="flex flex-col items-center gap-3">
        <span class="loading loading-spinner loading-lg"></span>
        <p class="text-sm text-base-content/80">{locale?.t('admin_drive_loading')}</p>
      </div>
    </div>

    <!-- Error State -->
    <div if="{state.error && !state.loading}" class="alert alert-error">
      <i class="icon-warning text-xl"></i>
      <span class="text-sm">{state.error}</span>
      <button onclick="{retryLoadDrives}" class="btn btn-sm btn-ghost">
        <i class="icon-refresh"></i>
        Retry
      </button>
    </div>

    <!-- Search/Filter Toolbar -->
    <div if="{!state.loading && !state.error && state.allDrives.length > 0}" class="flex gap-2 items-center">
      <input
        id="shared-drive-search"
        type="search"
        placeholder="{locale?.t('admin_drive_search_placeholder')}"
        aria-label="{locale?.t('admin_drive_search_placeholder')}"
        class="input input-bordered input-sm flex-1"
        value="{state.searchQuery}"
        oninput="{(e) => handleSearch(e.target.value)}"
        onsearch="{(e) => handleSearch(e.target.value)}" />

      <button
        onclick="{clearFilter}"
        class="btn btn-sm btn-outline"
        disabled="{!state.searchQuery}">
        <i class="icon-clear me-1"></i>
        {locale?.t('clearfilter')}
      </button>
    </div>

    <!-- Drives List Container with Scrollable Area -->
    <div if="{!state.loading && !state.error && state.allDrives.length > 0}" class="drives-list-container flex flex-col gap-0">
      <!-- Scrollable Drives List -->
      <div class="drives-list-scroll space-y-1.5 overflow-y-auto" style="max-height: 400px;">
        <!-- One click selects the drive and opens it - there is no separate
             confirm step, so each row is a native button rather than a radio
             in a card. Two lines: the name, then everything else as one muted
             meta line, which is what keeps the row short. -->
        <button each="{drive in state.drives}"
                type="button"
                class="drive-item w-full text-start bg-base-100 rounded-lg border border-base-300 hover:border-primary hover:bg-base-200 transition-colors overflow-hidden disabled:opacity-60"
                disabled="{state.savingSelection}"
                aria-busy="{state.savingDriveKey === driveKey(drive)}"
                onclick="{() => selectDrive(drive)}">

        <div class="flex items-center gap-3 w-full py-2 px-3">
          <!-- Leading glyph, tinted with the drive's Google-assigned color when
               it has one and muted when it does not. This carries the color that
               used to sit in a separate square swatch: decorative either way, so
               hidden from screen readers, and the name still does the
               identifying. The SharePoint list has the same slot, which is what
               keeps the two pickers looking like one another. -->
          <i class="icon-drive text-lg flex-shrink-0 {drive.colorRgb ? '' : 'text-base-content/40'}"
             aria-hidden="true"
             style="{drive.colorRgb ? 'color: ' + drive.colorRgb : ''}"></i>

          <!-- Drive Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-sm text-base-content truncate">{drive.name}</span>
              <span if="{drive.isDefault}" class="badge badge-primary badge-xs flex-shrink-0">{locale?.t('filepicker_my_drive')}</span>
            </div>
            <p if="{driveMeta(drive)}" class="text-xs text-base-content/70 truncate">{driveMeta(drive)}</p>
          </div>

          <span if="{state.savingDriveKey === driveKey(drive)}" class="loading loading-spinner loading-xs flex-shrink-0"></span>
          <i if="{state.savingDriveKey !== driveKey(drive)}" class="icon-arrow_forward text-base-content/50 flex-shrink-0" aria-hidden="true"></i>
        </div>
      </button>
      </div>

    <!-- Empty State (no drives at all) -->
    <div if="{!state.loading && !state.error && state.allDrives.length === 0}" class="alert alert-warning">
      <i class="icon-error_outline text-xl"></i>
      <span class="text-sm">{locale?.t('admin_drive_nodrivesavailable')}</span>
    </div>

    <!-- No Search Results -->
    <div if="{!state.loading && !state.error && state.allDrives.length > 0 && state.drives.length === 0 && state.searchQuery}" class="card bg-base-100 mb-6">
      <div class="card-body text-center py-6">
        <i class="icon-search text-6xl text-gray-300 mb-2"></i>
        <p class="text-gray-500">No drives match "{state.searchQuery}"</p>
      </div>
    </div>

    </div>

    <!-- OAuth Reauthorization Modal -->
    <div if="{state.showOAuthModal}" class="modal modal-open">
      <div class="modal-box">
        <h3 class="font-bold text-lg mb-4">
          <i class="icon-warning text-warning me-2"></i>
          Repository Connection Expired
        </h3>
        <p class="py-4">{state.oauthErrorMessage}</p>
        <div class="modal-action gap-2">
          <button
            onclick="{closeOAuthModal}"
            class="btn btn-ghost">
            {locale?.t('cancel')}
          </button>
          <button
            onclick="{reauthorizeRepository}"
            class="btn btn-primary">
            <i class="icon-refresh me-1"></i>
            Re-authorize Now
          </button>
        </div>
      </div>
      <button type="button" aria-label="{locale?.t('close')}" class="modal-backdrop" onclick="{closeOAuthModal}"></button>
    </div>
  </div>

  <script>
    import { getRepositoryService } from '../../../services/service-loader';

    export default {

      props: {
        repositoryId: null,
        provider: null,
        onDriveSelected: null,
        mode: 'file'
      },

      state: {
        drives: [],
        allDrives: [],
        selectedDrive: null,
        loading: false,
        error: null,
        savingSelection: false,
        // Which row is mid-save, so the spinner lands on the drive that was
        // clicked rather than on the list as a whole.
        savingDriveKey: null,
        showOAuthModal: false,
        oauthErrorMessage: '',
        oauthErrorCallback: null,
        searchQuery: ''
      },

      async onMounted() {
        if (this.props.repositoryId && this.props.provider === 'google_drive') {
          await this.loadSharedDrives();
        }
      },

      async loadSharedDrives() {
        try {
          this.update({ loading: true, error: null });

          // Use repository service to fetch shared drives
          const repositoryService = await getRepositoryService();
          const data = await repositoryService.getSharedDrives(this.props.repositoryId);
          
          const drives = data.drives || [];

          // Nothing is pre-selected: a click now opens the drive outright, so
          // pre-selecting one would only make the first row look special
          // without doing anything.
          this.update({
            drives,
            allDrives: drives,
            selectedDrive: null,
            loading: false
          });
        } catch (error) {
          console.error('Failed to load Shared Drives:', error);
          
          // Check if it's an OAuth token expiration error
          if (error.response?.status === 401 && error.response?.data?.error === 'oauth_token_expired') {
            this.showOAuthExpiredModal(
              error.response.data.message || 'Your repository connection has expired.',
              () => this.loadSharedDrives()
            );
            this.update({ loading: false });
            return;
          }
          
          // If endpoint not found (404), skip drive selection and proceed to file browsing
          if (error.response?.status === 404) {
            console.warn('Shared drives endpoint not available. Proceeding to file browsing...');
            this.update({ loading: false });
            if (this.props.onDriveSelected) {
              this.props.onDriveSelected({
                driveId: 'root',
                driveName: 'My Drive'
              });
            }
            return;
          }
          
          // On other errors, proceed to file browsing with root/My Drive
          if (this.props.onDriveSelected) {
            this.props.onDriveSelected({
              driveId: 'root',
              driveName: 'My Drive'
            });
          }
          this.update({ loading: false });
        }
      },

      /** Stable per-row key. A shared drive has an id; My Drive's is null. */
      driveKey(drive) {
        return drive?.id || 'root';
      },

      /**
       * The muted second line of a row: created date and capabilities, joined
       * rather than stacked as badge pills. This is what makes the row two
       * lines instead of four.
       */
      driveMeta(drive) {
        const parts = [];
        if (drive.createdTime) {
          const date = this.tools?.formatDate(drive.createdTime) || drive.createdTime;
          parts.push(`${this.locale?.t('created') || 'Created'} ${date}`);
        }
        const caps = drive.capabilities || {};
        if (caps.canCopy) parts.push(this.locale?.t('admin_drive_capabilities_copy'));
        if (caps.canDownload) parts.push(this.locale?.t('admin_drive_capabilities_download'));
        if (caps.canEdit) parts.push(this.locale?.t('admin_drive_capabilities_edit'));
        return parts.filter(Boolean).join(' · ');
      },

      /**
       * Selecting a drive opens it. There is no separate confirm step: the
       * old two-stage flow (pick a row, then press Browse Files) asked for a
       * second click that carried no extra decision.
       */
      async selectDrive(drive) {
        if (!drive || this.state.savingSelection) return;

        try {
          this.update({
            selectedDrive: drive,
            savingSelection: true,
            savingDriveKey: this.driveKey(drive)
          });

          // Use dedicated Google Drive selection endpoint to avoid TSOA union validation issues
          const repositoryService = await getRepositoryService();
          const updateResponse = await repositoryService.selectGoogleDrive(this.props.repositoryId, {
            sharedDriveId: drive.id,
            sharedDriveName: drive.name
          });

          console.log('Shared drive selection saved:', {
            driveId: drive.id,
            driveName: drive.name,
            response: updateResponse
          });

          this.update({ savingSelection: false, savingDriveKey: null });

          // Notify parent component with full drive info
          if (this.props.onDriveSelected) {
            this.props.onDriveSelected({
              driveId: drive.id,
              driveName: drive.name,
              name: drive.name,
              displayName: drive.name,
              // The drive's Google-configured artwork, shown as a banner behind the
              // selected-drive bar once browsing starts. Absent for My Drive and for
              // any shared drive with no theme set.
              backgroundImageLink: drive.backgroundImageLink || null,
              savedConfig: updateResponse
            });
          }
        } catch (error) {
          console.error('Failed to save Shared Drive selection:', error);

          // Check if it's an OAuth token expiration error
          if (error.response?.status === 401 && error.response?.data?.error === 'oauth_token_expired') {
            this.update({ savingSelection: false, savingDriveKey: null });
            this.showOAuthExpiredModal(
              error.response.data.message || 'Your repository connection has expired.',
              () => this.selectDrive(drive)
            );
            return;
          }

          alert(this.locale.t('alert_sharepoint_save_failed') + ': ' + (error.message || 'Unknown error'));
          this.update({ savingSelection: false, savingDriveKey: null });
        }
      },

      retryLoadDrives() {
        this.loadSharedDrives();
      },

      showOAuthExpiredModal(errorMessage, retryCallback) {
        // Show OAuth re-auth modal and store the callback to retry the original action
        this.update({
          showOAuthModal: true,
          oauthErrorMessage: errorMessage || 'Your repository connection has expired. Please re-authorize to continue.',
          oauthErrorCallback: retryCallback
        });
      },

      async reauthorizeRepository() {
        try {
          // Import the repository service to get OAuth URL
          const repositoryService = await getRepositoryService();

          // Get OAuth URL
          const oauthData = await repositoryService.getOAuthUrl(this.props.repositoryId);
          
          if (oauthData?.authorizationUrl) {
            // Create a promise that resolves when OAuth completes
            const oauthPromise = new Promise((resolve) => {
              const messageHandler = (event) => {
                // Verify message is from our origin
                if (event.origin !== window.location.origin) return;
                
                // Check for OAuth completion message
                if (event.data?.type === 'repository-connected') {
                  window.removeEventListener('message', messageHandler);
                  resolve(event.data);
                }
              };
              
              // Listen for postMessage from OAuth callback popup
              window.addEventListener('message', messageHandler);
              
              // Timeout after 5 minutes if no response
              setTimeout(() => {
                window.removeEventListener('message', messageHandler);
                resolve({ success: false, timeout: true });
              }, 5 * 60 * 1000);
            });
            
            // Open OAuth popup
            window.open(oauthData.authorizationUrl, 'oauth-popup', 'width=500,height=600');
            
            // Wait for OAuth callback
            const result = await oauthPromise;
            
            // Close modal
            this.update({ showOAuthModal: false });
            
            if (result.success !== false) {
              // OAuth succeeded, retry the original action
              const callback = this.state.oauthErrorCallback;
              if (callback && typeof callback === 'function') {
                await callback();
              }
            } else if (result.timeout) {
              alert(this.locale.t('alert_oauth_timeout'));
            } else {
              alert(this.locale.t('alert_oauth_failed') + ': ' + (result.error || 'Unknown error'));
            }
          } else {
            alert(this.locale.t('alert_oauth_url_failed'));
          }
        } catch (error) {
          console.error('Failed to re-authorize:', error);
          alert(this.locale.t('alert_oauth_reauth_failed') + ': ' + (error.message || 'Unknown error'));
        }
      },

      closeOAuthModal() {
        this.update({ showOAuthModal: false });
      },

      handleSearch(query) {
        this.update({ searchQuery: query });
        this.filterDrives(query);
      },

      clearFilter() {
        this.update({ searchQuery: '' });
        this.filterDrives('');
      },

      filterDrives(query) {
        const searchQuery = query.toLowerCase();
        if (!searchQuery) {
          // No query - show all drives
          this.update({ drives: this.state.allDrives });
          return;
        }

        // Filter drives by name
        const filteredDrives = this.state.allDrives.filter(drive =>
          drive.name?.toLowerCase().includes(searchQuery)
        );
        this.update({ drives: filteredDrives });
      },

    }
  </script>

  <style>
    .shared-drive-selector-container {
      font-family: inherit;
    }

    /* Container for scrollable drives list */
    .drives-list-container {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    /* Scrollable area with max height */
    .drives-list-scroll {
      flex: 1;
      overflow-y: auto;
      padding-right: 8px; /* Account for scrollbar */
    }

    .drive-item {
      display: block;
    }
  </style>
</shared-drive-selector>
